import React from "react";
import { Link, useNavigate } from "react-router-dom";
import favIcon from "../Assets/favicon.ico";
import { NavigationHelper } from "../Components/Card/NavigationHelper";
import { happeningNowLinks, sendThroughLogin } from "../helper/happeningNow";
import { signedIn } from "./session";
import { dayLabel, daysLeft, daysLeftLabel, dueLine, eventDates, kindOf, placeOf } from "./siteData";

// Event and result cards for the public home page.

// A missing or broken logo shows the shamrock instead of an empty circle.
export const crestFallback = (ev) => {
  if (ev.currentTarget.dataset.fallback) return;
  ev.currentTarget.dataset.fallback = "1";
  ev.currentTarget.src = favIcon;
};

export const Crest = ({ event, size = "" }) => (
  <img className={`crest${size}`} src={event?.logo || favIcon} alt="" loading="lazy" onError={crestFallback} />
);

// Same destinations as the app's own cards (Components/Card/NavigationHelper).
export const resultsTo = (event) => {
  const slug = (event?.name || "").split(" ").join("+");
  const base = event?.eventType === "Oireachtas" ? "/oireachtas/results" : "/results";
  return { pathname: `${base}/${slug}`, search: `?id=${event?._id}` };
};

const DAYS_LEFT_TAG = 14;

// Register skips the event page and opens registration itself (Devin
// 2026-10-16). Signed out, it goes through log in, which comes back to the
// saved path with the event (Parent/Auth/SignIn.jsx), the same keys the event
// page's own Register button saves.
export const registerPath = (event) => (event?.eventType === "Grade Exam"
  ? `/register-grade-exam?id=${event?._id}`
  : `/register-dancers?id=${event?._id}`);

const goRegister = (event, navigate) => {
  const path = registerPath(event);
  if (signedIn()) {
    navigate(path, { state: { event } });
    return;
  }
  localStorage.setItem("path", path);
  localStorage.setItem("eventId", event?._id);
  localStorage.setItem("eventType", event?.eventType);
  localStorage.setItem("from", "open");
  navigate("/login");
};

// A click anywhere else on an event card opens the event page (live results
// for today's events). The button stays the keyboard and screen-reader path;
// clicks on it, on a link, or at the end of a text selection are left alone.
const cardClick = (go) => (ev) => {
  if (ev.target.closest("a, button")) return;
  if (window.getSelection && String(window.getSelection())) return;
  go();
};

export const UpcomingCard = ({ item, now }) => {
  const navigate = useNavigate();
  const e = item.event;
  const due = dueLine(item);
  const open = item.state === "open" || item.state === "late";
  const left = open && due.close ? daysLeft(due.close, now) : null;
  // Oireachtas entries are made by teachers; families get the event page.
  const register = open && e.eventType !== "Oireachtas";
  const go = () => NavigationHelper(e, item.from, navigate);
  return (
    <article className={`ev glow rv${item.state === "late" ? " is-late" : open ? " is-open" : ""}`} onClick={cardClick(go)}>
      <Crest event={e} size=" xl" />
      <div>
        <span className="eyebrow">{kindOf(e)}</span>
        <h3>{e.name}</h3>
        <p className="meta">
          {eventDates(e)}
          <br />
          {[e.venue_name, placeOf(e)].filter(Boolean).join(" · ")}
        </p>
      </div>
      <div className="foot">
        <p className={`due ${due.tone === "open" ? "open" : due.tone === "closed" ? "closed" : "soon"}`}>
          <i />
          <span>{due.text}</span>
          {left !== null && left >= 0 && left <= DAYS_LEFT_TAG && <> <span className="left">{daysLeftLabel(left)}</span></>}
        </p>
        <button
          type="button"
          className={`sbtn${register ? (item.state === "late" ? " late" : "") : " ghost"}`}
          onClick={register ? () => goRegister(e, navigate) : go}
        >
          {register ? "Register" : "Event details"}
        </button>
      </div>
    </article>
  );
};

// Happening now: live results, competitor lists and schedule side by side
// (helper/happeningNow); results lead, the others are ghost buttons.
export const TodayCard = ({ item }) => {
  const navigate = useNavigate();
  const e = item.event;
  const go = () => NavigationHelper(e, "result", navigate);
  const links = happeningNowLinks(e);
  return (
    <article className="ev today glow rv" onClick={cardClick(go)}>
      <Crest event={e} size=" xl" />
      <div>
        <span className="eyebrow">{kindOf(e)}</span>
        <h3>{e.name}</h3>
        <p className="meta">{[eventDates(e), placeOf(e)].filter(Boolean).join(" · ")}</p>
      </div>
      <div className="foot">
        <div className="now-links">
          {links.map((l, i) => (l.href ? (
            <a key={l.key} className="sbtn ghost" href={l.href} target="_blank" rel="noopener noreferrer">{l.label}</a>
          ) : (
            <Link
              key={l.key}
              className={`sbtn${i ? " ghost" : ""}`}
              to={l.to}
              state={{ event: e, from: "result" }}
              onClick={l.login && !signedIn() ? (ev) => { ev.preventDefault(); sendThroughLogin(e, l, navigate); } : undefined}
            >
              {i === 0 ? "Live results" : l.label}
            </Link>
          )))}
        </div>
      </div>
    </article>
  );
};

export const ResultCard = ({ card, index }) => {
  const [first] = card.events;
  const many = card.events.length > 1;
  return (
    <article className="sc glow rv" style={{ "--i": index }}>
      <Crest event={first} />
      <div className="sc-t">
        <span className="eyebrow">{card.kind}</span>
        <Link className="sc-name" to={resultsTo(first)} state={{ event: first, from: "result" }}>{card.name}</Link>
        <span className="meta">{[card.dates, card.place].filter(Boolean).join(" · ")}</span>
        <p className="sc-links">
          {many ? card.events.map((e, i) => (
            <React.Fragment key={e._id}>
              {i > 0 && " · "}
              <Link to={resultsTo(e)} state={{ event: e, from: "result" }}>
                {`${dayLabel(e.name) ? dayLabel(e.name).slice(0, 3) : `Day ${i + 1}`} results`}
              </Link>
            </React.Fragment>
          )) : (
            <Link to={resultsTo(first)} state={{ event: first, from: "result" }}>Results</Link>
          )}
        </p>
      </div>
    </article>
  );
};
